import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Table } from '../../../shared/components/Table';
import { Modal } from '../../../shared/components/Modal';
import ShadButton from '../../../shared/components/Button';
import { Badge } from '../../blog/components/ui';
import type { ContentPage } from '../types';
import { CONTENT_PAGE_STATUS_LABELS, derivePageStatus } from '../payload';

const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const statusBadgeVariant = (page: ContentPage): 'secondary' | 'default' | 'outline' => {
  const status = derivePageStatus(page);
  if (status === 'DRAFT') return 'outline';
  if (status === 'PUBLISHED_EDITED') return 'secondary';
  return 'default';
};

interface ContentPagesTableProps {
  pages: ContentPage[];
  isLoading: boolean;
  canManage: boolean;
  onDelete: (page: ContentPage) => void;
  isDeleting: boolean;
}

export function ContentPagesTable({ pages, isLoading, canManage, onDelete, isDeleting }: ContentPagesTableProps) {
  const navigate = useNavigate();
  const [pendingDelete, setPendingDelete] = useState<ContentPage | null>(null);

  const columns: ColumnDef<ContentPage>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => <span className="font-medium text-foreground">{row.original.title}</span>,
    },
    {
      accessorKey: 'slug',
      header: 'Slug',
      cell: ({ row }) => <span className="text-muted-foreground">{`/pages/${row.original.slug}`}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={statusBadgeVariant(row.original)}>{CONTENT_PAGE_STATUS_LABELS[derivePageStatus(row.original)]}</Badge>,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last updated',
      cell: ({ row }) => <span className="text-muted-foreground">{formatDate(row.original.updatedAt)}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/content-pages/${row.original.id}`)}
            className="text-blue-500 hover:underline text-[14px]"
          >
            Edit
          </button>
          {canManage && (
            <button
              onClick={() => setPendingDelete(row.original)}
              className="text-red-500 hover:underline text-[14px]"
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Table data={pages} columns={columns} isLoading={isLoading} storageKey="content-pages-table" />

      <Modal isOpen={!!pendingDelete} onClose={() => setPendingDelete(null)} size="sm">
        <div className="p-6">
          <h2 className="text-[18px] font-bold text-foreground">Delete page</h2>
          <p className="text-[14px] text-muted-foreground mt-2">
            Delete “{pendingDelete?.title}”? This cannot be undone
            {pendingDelete && derivePageStatus(pendingDelete) !== 'DRAFT' ? ' and immediately removes it from the storefront.' : '.'}
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <ShadButton variant="outline" size="sm" onClick={() => setPendingDelete(null)}>
              Cancel
            </ShadButton>
            <ShadButton
              variant="primary"
              size="sm"
              className="text-red-500"
              disabled={isDeleting}
              loading={isDeleting}
              onClick={() => {
                if (pendingDelete) onDelete(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Delete
            </ShadButton>
          </div>
        </div>
      </Modal>
    </>
  );
}
